import {
  TaskFragmentGQLType,
  TaskStatusGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { ColorScheme } from 'packages/common-frontend/theme';
import { truncateUuid } from 'packages/common-frontend/utils';

// id that will be used to group unassigned tasks together
export const UNASSIGNED_USER = 'unassigned_user_id';

export const taskStatuses = [
  'overdue',
  'dueSoon',
  'completed',
  'archived',
] as const; // this is no better way to write out the order of these four statuses (can't figure out a way to do this using the gql type)
export type TaskStatus = typeof taskStatuses[number];

export const statusColorScheme: { [key in TaskStatusGQLType]: ColorScheme } = {
  overdue: 'red',
  dueSoon: 'orange',
  completed: 'green',
  archived: 'gray',
};

export const statusIcon: { [key in TaskStatusGQLType]: string } = {
  overdue: 'calendar-clock',
  dueSoon: 'calendar-clock',
  completed: 'circle-check',
  archived: 'circle-x',
};

export const getTrucatedLoanIdsFromCategorySpecificProperties = (
  props: TaskFragmentGQLType['domainProperties']['categorySpecificProperties'],
): string => {
  switch (props.__typename) {
    case 'FundingRequestTaskSpecificProperties':
      if (props.loanIds.length > 0) {
        return props.loanIds.map((loanId) => truncateUuid(loanId)).join(', ');
      }
      return '-';
    case 'InvoiceTaskSpecificProperties':
    case 'ConsolidatedDisbursementTaskProperties':
      if (props.invoiceLoanIds.length > 0) {
        return props.invoiceLoanIds
          .map((loanId) => truncateUuid(loanId))
          .join(', ');
      }
      return '-';
    case 'LenderSharesTaskSpecificProperties':
      return truncateUuid(props.lenderSharesLoanId);
    default:
      throw Error(
        `[Tasks] CategorySpecificPropertiesGQLType should always have a __typename`,
      );
  }
};

export const getActionRequiredFromCategorySpecificProperties = (
  props: TaskFragmentGQLType['domainProperties']['categorySpecificProperties'],
): string => {
  switch (props.__typename) {
    case 'ConsolidatedDisbursementTaskProperties':
      return 'Mark Disbursed';
    case 'FundingRequestTaskSpecificProperties':
      return 'Approve';
    case 'InvoiceTaskSpecificProperties':
      return 'Mark Paid';
    case 'LenderSharesTaskSpecificProperties':
      return 'Mark Funded';
    default:
      throw Error(
        `[Tasks] CategorySpecificPropertiesGQLType should always have a __typename`,
      );
  }
};

export const getDomainEntityDueDateForTask = (
  task: TaskFragmentGQLType,
): Date | undefined => {
  const props = task.domainProperties.categorySpecificProperties;
  switch (props.__typename) {
    case 'FundingRequestTaskSpecificProperties':
      return new Date(props.fundingRequestDueDate);
    case 'InvoiceTaskSpecificProperties':
      return new Date(props.invoiceDueDate);
    case 'ConsolidatedDisbursementTaskProperties':
      return undefined;
    case 'LenderSharesTaskSpecificProperties':
      return props.lenderSharesDueDate
        ? new Date(props.lenderSharesDueDate)
        : undefined;
    default:
      throw Error(
        `[Tasks] CategorySpecificPropertiesGQLType should always have a __typename`,
      );
  }
};

// currently the only task with a upper limit on due date configuration is FR tasks
// if/when other domains need to enforce max/min on the task.dueDate configuration,
// we can update this function as needed
export const getMaxDomainDueDateForTask = (
  task: TaskFragmentGQLType,
): Date | undefined => {
  const props = task.domainProperties.categorySpecificProperties;
  switch (props.__typename) {
    case 'FundingRequestTaskSpecificProperties':
      return getDomainEntityDueDateForTask(task);
    case 'ConsolidatedDisbursementTaskProperties':
    case 'InvoiceTaskSpecificProperties':
    case 'LenderSharesTaskSpecificProperties':
      return undefined;
    default:
      throw Error(
        `[Tasks] CategorySpecificPropertiesGQLType should always have a __typename`,
      );
  }
};
